import { useRecordActions } from '@/features/RecordActions/model/useRecordActions';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TabPanel, TabView } from 'primereact/tabview';
import { useUserDialogStore } from '@/entities/UserDialog/model/store';
import { DIALOG_ACTION_OPTIONS } from '@/entities/UserDialog/config/dialog.config';
import { UserTableToolbar } from '@/shared/ui/UserTableToolbar/UserTableToolbar';
import { CreateButton } from '@/shared/ui/CreateButton/CreateButton';
import { DataTable } from '@/shared/ui/DataTable/DataTable';
import { RequestErrorBanner } from '@/shared/ui/RequestState';
import { RequestStatus } from '@/shared/model/requestStatus';
import styles from './UserDialogs.module.scss';

/** Показывает шаблоны и выпуски диалоговых окон */
export const UserDialogsPage = () => {
    const templateActions = useRecordActions('dialogTemplates', () => loadTemplates());

    const broadcastActions = useRecordActions('dialogBroadcasts', () => loadBroadcasts({
        skipCount: first,
        takeCount: 10
    }));

    const { templates, broadcasts, loadTemplates, loadBroadcasts } = useUserDialogStore();
    const [first, setFirst] = useState(0);
    const navigate = useNavigate();
    useEffect(() => { void loadTemplates(); }, [loadTemplates]);

    useEffect(() => {
        void loadBroadcasts({
            skipCount: first,
            takeCount: 10
        });
    }, [first, loadBroadcasts]);

    return <main className={styles.page}>
        <h1>Диалоговые окна</h1>
        <TabView
            className={styles.tabs}
            panelContainerClassName={styles.tabContent}
        >
            <TabPanel header="Шаблоны">
                <RequestErrorBanner
                    {...templates}
                    fallbackMessage="Не удалось загрузить шаблоны"
                    onRetry={loadTemplates}
                />
                <DataTable
                    rowActions={templateActions.getMenuItems}
                    getRowUrl={item => item.id
                        ? `/user-dialogs/templates/${encodeURIComponent(item.id)}`
                        : undefined}
                    header={<UserTableToolbar
                        createButton={<CreateButton
                            label="Создать шаблон"
                            navigateTo="/user-dialogs/templates/new"
                        />}
                    />}
                    data={templates.data ?? []}
                    columns={[
                        {
                            field: 'title',
                            header: 'Заголовок'
                        },
                        {
                            field: 'action',
                            header: 'Действие',
                            body: item => DIALOG_ACTION_OPTIONS.find(option => option.id === item.action)?.name ?? '—'
                        },
                        {
                            field: 'isEnabled',
                            header: 'Доступен',
                            body: item => item.isEnabled
                                ? 'Да'
                                : 'Нет'
                        },
                    ]}
                    loading={templates.status === RequestStatus.Loading}
                    onRowClick={item => navigate(`/user-dialogs/templates/${item.id}`)}
                />
            </TabPanel>
            <TabPanel header="Выпуски">
                <RequestErrorBanner
                    {...broadcasts}
                    fallbackMessage="Не удалось загрузить выпуски"
                    onRetry={() => loadBroadcasts({
                        skipCount: first,
                        takeCount: 10
                    })}
                />
                <DataTable
                    rowActions={broadcastActions.getMenuItems}
                    getRowUrl={item => item.id
                        ? `/user-dialogs/broadcasts/${encodeURIComponent(item.id)}`
                        : undefined}
                    header={<UserTableToolbar
                        createButton={<CreateButton
                            label="Создать выпуск"
                            navigateTo="/user-dialogs/broadcasts/new"
                        />}
                    />}
                    data={broadcasts.data?.items ?? []}
                    columns={[
                        {
                            field: 'name',
                            header: 'Выпуск'
                        },
                        {
                            field: 'templateTitle',
                            header: 'Шаблон'
                        },
                        {
                            field: 'isSystem',
                            header: 'Тип',
                            body: item => item.isSystem
                                ? 'Системный'
                                : 'Ручной'
                        },
                        {
                            field: 'filterDescription',
                            header: 'Аудитория'
                        },
                    ]}
                    loading={broadcasts.status === RequestStatus.Loading}
                    onRowClick={item => navigate(`/user-dialogs/broadcasts/${item.id}`)}
                    paginator
                    lazy
                    rows={10}
                    first={first}
                    totalRecords={broadcasts.data?.allCount ?? 0}
                    rowsPerPageOptions={[10]}
                    onPageChange={event => setFirst(event.first)}
                />
            </TabPanel>
        </TabView>
        {templateActions.dialogs}
        {broadcastActions.dialogs}
    </main>;
};
